import { z } from 'zod';

/* ─────────── Помощь, поддержка и предложения (раздел 6.22: БЗ-01…БЗ-03, БЗ-21…БЗ-24, БЗ-28…БЗ-30) ─────────── */

const Id = z.uuid();
const Ts = z.string();

/** Вид обращения (БЗ-23) */
export const TICKET_KINDS = {
  question: 'Вопрос',
  bug: 'Ошибка',
  access: 'Доступ',
  data: 'Данные',
  suggestion: 'Предложение',
} as const;
export type TicketKind = keyof typeof TICKET_KINDS;
export const TicketKind = z.enum(Object.keys(TICKET_KINDS) as [TicketKind, ...TicketKind[]]);

/** Срочность: «останавливает работу» — до 2 часов, остальное — до 4 рабочих часов (БЗ-24) */
export const TICKET_URGENCIES = {
  blocking: 'Останавливает работу',
  normal: 'Работа не стоит',
} as const;
export type TicketUrgency = keyof typeof TICKET_URGENCIES;
export const TicketUrgency = z.enum(Object.keys(TICKET_URGENCIES) as [TicketUrgency, ...TicketUrgency[]]);

/** Статусы обращения (БЗ-23) */
export const TICKET_STATUSES = {
  new: 'Новое',
  in_progress: 'В работе',
  waiting_user: 'Ждёт пользователя',
  waiting_owner: 'Ждёт владельца',
  with_dev: 'У разработки',
  answered: 'Отвечено',
  closed: 'Закрыто',
} as const;
export type TicketStatus = keyof typeof TICKET_STATUSES;
export const TicketStatus = z.enum(Object.keys(TICKET_STATUSES) as [TicketStatus, ...TicketStatus[]]);

/** Обращение не закрыто: у него ещё есть срок, исполнитель и дела */
export const TICKET_OPEN_STATUSES: TicketStatus[] = [
  'new',
  'in_progress',
  'waiting_user',
  'waiting_owner',
  'with_dev',
  'answered',
];

/** Критерий приёмки ПМ-11: первый ответ в срок не менее чем в 95 % обращений, открытых ошибок старше 5 рабочих дней нет */
export const PM11_IN_TIME_TARGET = 95;
export const PM11_OLD_BUG_WORKING_DAYS = 5;

/** Сроки первого ответа человека (БЗ-24) */
export const SLA_BLOCKING_HOURS = 2;
export const SLA_NORMAL_WORKING_HOURS = 4;
/** За сколько минут до срока исполнитель получает напоминание */
export const SLA_REMIND_BEFORE_MINUTES = 30;

/** Часы работы поддержки по умолчанию: рабочие дни РК, с 9 до 18 по часам компании */
export const SUPPORT_HOURS_DEFAULT = { startHour: 9, endHour: 18 } as const;

export const SupportSettings = z.object({
  startHour: z.number().int().min(0).max(23),
  endHour: z.number().int().min(1).max(24),
});
export type SupportSettings = z.infer<typeof SupportSettings>;

export const SupportSettingsInput = SupportSettings.refine((v) => v.endHour - v.startHour >= 4, {
  message: 'Часы поддержки — не короче четырёх часов: столько отводится на первый ответ',
  path: ['endHour'],
});

/** Номер обращения: ОБР-0007 */
export const ticketNumber = (seq: number) => `ОБР-${String(seq).padStart(4, '0')}`;
/** Номер предложения: ПРД-0007 */
export const suggestionNumber = (seq: number) => `ПРД-${String(seq).padStart(4, '0')}`;

const PagePath = z
  .string()
  .trim()
  .max(300)
  .regex(/^\/[A-Za-z0-9/_\-.?=&%#]*$/, 'Страница — путь внутри портала');
/** Путь страницы портала: так же проверяется адрес страницы в запросе похожих предложений («Сообщить») */
export const SupportPagePath = PagePath;

/**
 * Обращений и предложений от одного человека за час: кнопка «Сообщить» есть на каждой странице, лимит защищает
 * поддержку от случайной серии отправок и спама
 */
export const SUPPORT_HOURLY_LIMIT = 20;

/** Сообщение переписки с Pilot, которую человек прикладывает к обращению (БЗ-22) */
export const PilotLine = z.object({
  role: z.enum(['user', 'pilot']),
  text: z.string().trim().min(1).max(4000),
  /** Подпись сервера у ответа Pilot: без неё реплика «от Pilot» записывается как слова автора */
  sig: z.string().max(100).optional(),
});
export type PilotLine = z.infer<typeof PilotLine>;

/** Вопросов Pilot в «Помощи» от одного человека за час: каждый вопрос — вызов модели */
export const HELP_PILOT_HOURLY_LIMIT = 30;

/** Вопрос Pilot из панели «Помощь» (БЗ-01): к этой странице, с прежними репликами разговора */
export const HelpPilotAskInput = z.object({
  question: z.string().trim().min(3, 'Напишите вопрос хотя бы в три буквы').max(1000),
  path: PagePath.default('/'),
  history: z.array(PilotLine).max(10).default([]),
});
export type HelpPilotAskInput = z.infer<typeof HelpPilotAskInput>;

/** Ответ Pilot в «Помощи»: только со ссылками на инструкцию страницы или статьи базы знаний, которые человек видит */
export const HelpPilotAnswer = z.object({
  /** false — Pilot не нашёл ответа в инструкциях: вопрос лучше отправить в поддержку */
  answered: z.boolean(),
  text: z.string(),
  /** «Подготовлено ИИ» */
  label: z.string(),
  sources: z.array(z.object({ title: z.string(), path: z.string() })),
  /** Подпись ответа: возвращается вместе с перепиской в обращение или в «Помогло» */
  sig: z.string(),
});
export type HelpPilotAnswer = z.infer<typeof HelpPilotAnswer>;

/** «Помогло»: вопрос решён Pilot без человека — обращение создаётся закрытым (БЗ-23) */
export const HelpPilotResolvedInput = z.object({
  path: PagePath.default('/'),
  transcript: z.array(PilotLine).min(2).max(40),
});
export type HelpPilotResolvedInput = z.infer<typeof HelpPilotResolvedInput>;

export const TicketInput = z.object({
  subject: z.string().trim().min(3, 'Опишите тему хотя бы в трёх словах').max(200),
  kind: TicketKind,
  urgency: TicketUrgency.default('normal'),
  body: z.string().trim().min(3, 'Опишите, что случилось или что нужно').max(4000),
  /** Страница, с которой написали; портал подставляет её сам */
  pagePath: PagePath.nullable().optional(),
  pageTitle: z.string().trim().max(200).nullable().optional(),
  /** Устройство и размер окна, как их сообщил браузер */
  device: z.string().trim().max(300).nullable().optional(),
  projectId: Id.nullable().optional(),
  /** Переписка с Pilot, если человек сначала спрашивал его: Pilot подключается позже, поле уже принимается */
  pilotTranscript: z.array(PilotLine).max(40).optional(),
});
export type TicketInput = z.infer<typeof TicketInput>;

export const TicketSummary = z.object({
  id: Id,
  number: z.string(),
  subject: z.string(),
  kind: TicketKind,
  urgency: TicketUrgency,
  status: TicketStatus,
  authorId: Id,
  authorName: z.string(),
  assigneeId: Id.nullable(),
  assigneeName: z.string().nullable(),
  createdAt: Ts,
  updatedAt: Ts,
  /** Срок первого ответа человека */
  firstResponseDue: Ts.nullable(),
  firstResponseAt: Ts.nullable(),
  /** Срок первого ответа прошёл, а ответа нет */
  overdue: z.boolean(),
  escalated: z.boolean(),
  resolvedByPilot: z.boolean(),
  /** Автору есть что прочитать: ответ или смена статуса после его последнего просмотра */
  unread: z.boolean(),
});
export type TicketSummary = z.infer<typeof TicketSummary>;

export const TICKET_MESSAGE_KINDS = {
  user: 'Автор',
  support: 'Поддержка',
  pilot: 'Pilot',
  system: 'Портал',
} as const;
export type TicketMessageKind = keyof typeof TICKET_MESSAGE_KINDS;

export const TicketMessage = z.object({
  id: Id,
  kind: z.enum(['user', 'support', 'pilot', 'system']),
  authorId: Id.nullable(),
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: Ts,
});
export type TicketMessage = z.infer<typeof TicketMessage>;

export const TicketDetail = TicketSummary.extend({
  body: z.string(),
  pagePath: z.string().nullable(),
  pageTitle: z.string().nullable(),
  /** Роли автора на момент обращения */
  roleNames: z.string().nullable(),
  appVersion: z.string().nullable(),
  device: z.string().nullable(),
  projectId: Id.nullable(),
  projectCode: z.string().nullable(),
  escalatedToName: z.string().nullable(),
  closedAt: Ts.nullable(),
  messages: z.array(TicketMessage),
  /** Просматривающий — специалист поддержки: видит все поля и меняет статус и исполнителя */
  canManage: z.boolean(),
  canReply: z.boolean(),
  canClose: z.boolean(),
  /** Оценка ответа автором (БЗ-27); null — ещё не оценено */
  rating: z
    .object({
      solved: z.boolean(),
      score: z.number().int().min(1).max(5),
      comment: z.string().nullable(),
      at: Ts,
    })
    .nullable(),
  /** Автор может оценить ответ: обращение отвечено или закрыто, оценки ещё нет */
  canRate: z.boolean(),
  /** Передача разработке (БЗ-25): шаги воспроизведения видит автор и специалисты */
  dev: z
    .object({
      steps: z.string(),
      handedAt: Ts,
      handedByName: z.string().nullable(),
      releasedAt: Ts.nullable(),
      releaseNote: z.string().nullable(),
      /** Выпуск, в который войдёт исправление: идентификатор записи «Что нового»; при выкладке обращение закрывается */
      releaseId: z.string().nullable(),
      /** Название записи «Что нового», если выпуск уже выложен */
      releaseTitle: z.string().nullable(),
    })
    .nullable(),
  /** Специалист может передать ошибку разработке или отметить выпуск исправления */
  canHandoff: z.boolean(),
  canRelease: z.boolean(),
  /** Специалист может указать выпуск, с которым обращение закроется само */
  canLinkRelease: z.boolean(),
});
export type TicketDetail = z.infer<typeof TicketDetail>;

export const TicketMessageInput = z.object({
  body: z.string().trim().min(1, 'Напишите сообщение').max(4000),
  /** Только специалист поддержки: статус после ответа */
  status: TicketStatus.optional(),
});
export type TicketMessageInput = z.infer<typeof TicketMessageInput>;

export const TicketPatch = z
  .object({
    status: TicketStatus,
    assigneeId: Id.nullable(),
    urgency: TicketUrgency,
    kind: TicketKind,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Нечего менять' });
export type TicketPatch = z.infer<typeof TicketPatch>;

/** Оценка ответа: решён ли вопрос и от 1 до 5 (БЗ-27) */
export const TicketRatingInput = z.object({
  solved: z.boolean(),
  score: z.number().int().min(1, 'Оценка — от 1 до 5').max(5, 'Оценка — от 1 до 5'),
  comment: z.string().trim().max(1000).optional(),
});
export type TicketRatingInput = z.infer<typeof TicketRatingInput>;

/** Передача ошибки разработке: что делали, что ожидали, что получили (БЗ-25) */
export const TicketHandoffInput = z.object({
  steps: z
    .string()
    .trim()
    .min(20, 'Опишите шаги воспроизведения: что сделать, чтобы увидеть ошибку')
    .max(4000),
});
export type TicketHandoffInput = z.infer<typeof TicketHandoffInput>;

/** Исправление выпущено: что изменилось простыми словами — попадёт в «Что нового» (БЗ-25) */
export const TicketReleaseInput = z.object({
  note: z.string().trim().min(5, 'Напишите, что исправлено, простыми словами').max(1000),
});
export type TicketReleaseInput = z.infer<typeof TicketReleaseInput>;

/**
 * Идентификатор выпуска — поле `id` записи «Что нового» (releases.ts), например `2026-11-02-site-photo-fix`:
 * строчные латинские буквы, цифры и дефис
 */
export const ReleaseId = z
  .string()
  .trim()
  .regex(
    /^[a-z0-9][a-z0-9-]{2,99}$/,
    'Идентификатор выпуска — как поле id в «Что нового»: латиница, цифры, дефис',
  );

/**
 * Связать ошибку с выпуском (БЗ-25): когда запись «Что нового» с этим идентификатором выложена, обращение
 * закрывается само, автор получает уведомление
 */
export const TicketReleaseLinkInput = z.object({
  releaseId: ReleaseId,
  note: z.string().trim().min(5, 'Напишите, что исправлено, простыми словами').max(1000),
});
export type TicketReleaseLinkInput = z.infer<typeof TicketReleaseLinkInput>;

/** Итог закрытия обращений по выпуску */
export const ReleaseCloseResult = z.object({
  releaseId: z.string(),
  releaseTitle: z.string(),
  closed: z.number().int(),
});
export type ReleaseCloseResult = z.infer<typeof ReleaseCloseResult>;

/** Исправленная ошибка для «Что нового» (БЗ-25) */
export const ReleasedFix = z.object({
  id: Id,
  number: z.string(),
  subject: z.string(),
  releasedAt: Ts,
  note: z.string(),
  /** Обращение — моё или я специалист: только тогда есть ссылка */
  canOpen: z.boolean(),
  /** Выпуск «Что нового», с которым вышло исправление; null — выпуск отмечен вручную */
  releaseId: z.string().nullable(),
});
export type ReleasedFix = z.infer<typeof ReleasedFix>;

/** Строка отчёта: тема (раздел портала или роль автора) и сколько обращений */
export const SupportReportTopic = z.object({
  topic: z.string(),
  total: z.number().int(),
  /** Из них переданных человеку: не закрытых Pilot */
  byHuman: z.number().int(),
  unsolved: z.number().int(),
});
export type SupportReportTopic = z.infer<typeof SupportReportTopic>;

/** Отчёт поддержки за период (БЗ-27) */
export const SupportReport = z.object({
  days: z.number().int(),
  total: z.number().int(),
  /** Доля обращений, решённых Pilot без человека, %; null — обращений нет */
  pilotSolvedPercent: z.number().nullable(),
  avgFirstResponseMinutes: z.number().nullable(),
  /** Обращения, у которых первый ответ дан позже срока или срок прошёл без ответа */
  slaBreaches: z.number().int(),
  rated: z.number().int(),
  avgScore: z.number().nullable(),
  /** Доля оценок «вопрос решён», % */
  solvedPercent: z.number().nullable(),
  bySection: z.array(SupportReportTopic),
  byRole: z.array(SupportReportTopic),
  /** Темы, по которым вопрос чаще всего уходит человеку: список доработок инструкций и интерфейса */
  improvements: z.array(SupportReportTopic),
  /** Передано разработке за период и из них выпущено */
  handedToDev: z.number().int(),
  releasedFixes: z.number().int(),
  /**
   * Первый ответ в срок (ПМ-11): из обращений, которым нужен был ответ человека и срок которых наступил или ответ
   * уже дан, — доля ответивших в срок, %; null — таких обращений нет
   */
  inTimePercent: z.number().nullable(),
  inTimeCounted: z.number().int(),
  /** Открытые ошибки старше PM11_OLD_BUG_WORKING_DAYS рабочих дней — на любую дату, не только за период */
  oldBugs: z.number().int(),
  oldBugList: z.array(
    z.object({
      id: Id,
      number: z.string(),
      subject: z.string(),
      status: TicketStatus,
      workingDays: z.number().int(),
    }),
  ),
  /** Ошибки у разработки, ждущие выкладки указанного выпуска */
  waitingRelease: z.number().int(),
});
export type SupportReport = z.infer<typeof SupportReport>;

export const SupportAgent = z.object({ id: Id, fullName: z.string(), openTickets: z.number().int() });
export type SupportAgent = z.infer<typeof SupportAgent>;

export const SupportSummary = z.object({
  open: z.number().int(),
  overdue: z.number().int(),
  unassigned: z.number().int(),
  byStatus: z.record(z.string(), z.number().int()),
  /** Среднее время первого ответа за 30 дней, минут; null — ответов ещё не было */
  avgFirstResponseMinutes: z.number().nullable(),
  /** Доля ответов в срок за 30 дней, процентов; null — ответов ещё не было */
  inTimePercent: z.number().nullable(),
});
export type SupportSummary = z.infer<typeof SupportSummary>;

/* ───────────────────────── Предложения (БЗ-28…БЗ-30) ───────────────────────── */

export const SUGGESTION_STATUSES = {
  review: 'На рассмотрении',
  planned: 'В плане',
  in_progress: 'В разработке',
  released: 'Выпущено',
  rejected: 'Отклонено',
} as const;
export type SuggestionStatus = keyof typeof SUGGESTION_STATUSES;
export const SuggestionStatus = z.enum(
  Object.keys(SUGGESTION_STATUSES) as [SuggestionStatus, ...SuggestionStatus[]],
);

export const SuggestionInput = z.object({
  title: z.string().trim().min(3, 'Назовите предложение хотя бы в трёх словах').max(200),
  /**
   * Что неудобно. Необязательно: в панели «Сообщить» доработка пишется одним текстом «что улучшить и зачем» — он
   * уходит в «Что предлагается», а «Что неудобно» остаётся пустым
   */
  problem: z.string().trim().max(4000).default(''),
  /** Что предлагается */
  proposal: z.string().trim().min(3, 'Опишите, что предлагаете').max(4000),
  pagePath: PagePath.nullable().optional(),
});
export type SuggestionInput = z.infer<typeof SuggestionInput>;

export const SuggestionSummary = z.object({
  id: Id,
  number: z.string(),
  title: z.string(),
  status: SuggestionStatus,
  authorId: Id,
  authorName: z.string(),
  votes: z.number().int(),
  voted: z.boolean(),
  comments: z.number().int(),
  createdAt: Ts,
  statusChangedAt: Ts,
  /** Автору есть новое: смена статуса или комментарий после его последнего просмотра */
  unread: z.boolean(),
});
export type SuggestionSummary = z.infer<typeof SuggestionSummary>;

export const SuggestionEvent = z.object({
  id: Id,
  kind: z.enum(['comment', 'status']),
  authorId: Id.nullable(),
  authorName: z.string().nullable(),
  body: z.string(),
  fromStatus: SuggestionStatus.nullable(),
  toStatus: SuggestionStatus.nullable(),
  createdAt: Ts,
});
export type SuggestionEvent = z.infer<typeof SuggestionEvent>;

/* ── Оценка и повторы предложений (БЗ-32, БЗ-33) ── */

/** Шкала оценки пользы и трудоёмкости предложения */
export const SUGGESTION_SCALE = {
  1: 'очень низкая',
  2: 'низкая',
  3: 'средняя',
  4: 'высокая',
  5: 'очень высокая',
} as const;
export const SuggestionScore = z.number().int().min(1).max(5);

/** Голоса в приоритете считаются не больше этого числа: одно громкое предложение не вытесняет полезные */
export const PRIORITY_VOTES_CAP = 10;

/**
 * Приоритет предложения (БЗ-33): 2 × польза − трудоёмкость + голоса (не больше 10). Без оценки польза и
 * трудоёмкость считаются средними (3 и 3), поэтому неоценённое предложение стоит по голосам
 */
export function suggestionPriority(benefit: number | null, effort: number | null, votes: number): number {
  return 2 * (benefit ?? 3) - (effort ?? 3) + Math.min(Math.max(votes, 0), PRIORITY_VOTES_CAP);
}

/** Оценка специалиста поддержки: видят только специалисты */
export const SuggestionAssessment = z.object({
  benefit: SuggestionScore,
  effort: SuggestionScore,
  note: z.string().nullable(),
  byName: z.string().nullable(),
  at: Ts,
});
export type SuggestionAssessment = z.infer<typeof SuggestionAssessment>;

export const SuggestionAssessmentInput = z.object({
  benefit: SuggestionScore,
  effort: SuggestionScore,
  note: z.string().trim().max(1000).nullable().optional(),
});
export type SuggestionAssessmentInput = z.infer<typeof SuggestionAssessmentInput>;

/** Ссылка на предложение: номер и название */
export const SuggestionRef = z.object({ id: Id, number: z.string(), title: z.string() });
export type SuggestionRef = z.infer<typeof SuggestionRef>;

/** Повтор: предложение объединяется с основным, голоса переходят к основному (БЗ-32) */
export const SuggestionDuplicateInput = z.object({ of: Id });

/** Премия за предложение для карточки: номинация и период, без суммы (БЗ-35) */
export const SuggestionAwardBadge = z.object({ nomination: z.string(), periodLabel: z.string() });
export type SuggestionAwardBadge = z.infer<typeof SuggestionAwardBadge>;

export const SuggestionDetail = SuggestionSummary.extend({
  problem: z.string(),
  proposal: z.string(),
  pagePath: z.string().nullable(),
  statusReason: z.string().nullable(),
  releasedAt: Ts.nullable(),
  events: z.array(SuggestionEvent),
  canManage: z.boolean(),
  /** Оценка пользы и трудоёмкости (БЗ-33): только специалистам поддержки, остальным — null */
  assessment: SuggestionAssessment.nullable(),
  /** Приоритет для специалистов (БЗ-33); остальным — null */
  priority: z.number().int().nullable(),
  /** Предложение объединено с основным как повтор (БЗ-32) */
  duplicateOf: SuggestionRef.nullable(),
  /** Повторы, присоединённые к этому предложению */
  duplicates: z.array(SuggestionRef),
  /** Премии за это предложение (БЗ-35): номинация и период, без суммы */
  awards: z.array(SuggestionAwardBadge),
});
export type SuggestionDetail = z.infer<typeof SuggestionDetail>;

export const SuggestionStatusInput = z
  .object({
    status: SuggestionStatus,
    /** Причина: обязательна при отказе; при остальных статусах — пояснение автору */
    reason: z.string().trim().max(1000).nullable().optional(),
  })
  .refine((v) => v.status !== 'rejected' || (v.reason ?? '').trim().length >= 5, {
    message: 'Отказ без причины не принимается: напишите автору, почему',
    path: ['reason'],
  });
export type SuggestionStatusInput = z.infer<typeof SuggestionStatusInput>;

export const SuggestionCommentInput = z.object({ body: z.string().trim().min(1).max(2000) });

export const SimilarSuggestion = z.object({
  id: Id,
  number: z.string(),
  title: z.string(),
  status: SuggestionStatus,
  votes: z.number().int(),
  /** Просматривающий уже поддержал предложение («+1 мне тоже» в панели «Сообщить») */
  voted: z.boolean(),
  /** Предложение самого просматривающего: голосовать за своё не нужно, автор голосует первым сам */
  mine: z.boolean(),
  /** Предложение оставлено на этой же странице, а не просто в этом разделе или по похожим словам */
  samePage: z.boolean(),
});
export type SimilarSuggestion = z.infer<typeof SimilarSuggestion>;

/** Выпущенное предложение для «Что нового» (БЗ-30) */
export const ReleasedSuggestion = z.object({
  id: Id,
  number: z.string(),
  title: z.string(),
  releasedAt: Ts,
  authorName: z.string().nullable(),
  note: z.string().nullable(),
});
export type ReleasedSuggestion = z.infer<typeof ReleasedSuggestion>;

/* ───────────────────────── Помощь (БЗ-01…БЗ-03) ───────────────────────── */

export const HelpSection = z.object({ title: z.string(), items: z.array(z.string()) });
export type HelpSection = z.infer<typeof HelpSection>;

export const HelpFaq = z.object({ q: z.string(), a: z.string() });
export type HelpFaq = z.infer<typeof HelpFaq>;

export const HelpLink = z.object({ title: z.string(), path: z.string() });
export type HelpLink = z.infer<typeof HelpLink>;

/** Инструкция к странице для роли пользователя (БЗ-01, БЗ-02) */
export const HelpView = z.object({
  path: z.string(),
  /** Раздел, к которому относится страница; null — страница не из разделов (профиль, поддержка) */
  title: z.string(),
  summary: z.string(),
  roles: z.string(),
  sections: z.array(HelpSection),
  faq: z.array(HelpFaq),
  related: z.array(HelpLink),
  /** Pilot подключается позже: пока вопрос идёт в поддержку */
  pilot: z.object({ available: z.boolean() }),
});
export type HelpView = z.infer<typeof HelpView>;

/** Инструкция по роли целиком: все разделы, открытые человеку (БЗ-02) */
export const RoleGuide = z.object({
  roles: z.string(),
  intro: z.string(),
  modules: z.array(
    z.object({
      code: z.string(),
      title: z.string(),
      path: z.string(),
      summary: z.string(),
      youCan: z.array(z.string()),
      steps: z.array(z.string()),
    }),
  ),
});
export type RoleGuide = z.infer<typeof RoleGuide>;

export const SupportMe = z.object({
  /** Когда человек прошёл или закрыл экскурсию; null — ещё не показывали (БЗ-03) */
  tourDoneAt: Ts.nullable(),
  /** Специалист поддержки: видит очередь, меняет статусы предложений */
  isAgent: z.boolean(),
  /** Ждёт внимания автора: ответы по обращениям и новые статусы предложений */
  unread: z.number().int(),
});
export type SupportMe = z.infer<typeof SupportMe>;

/** Черновик статьи базы знаний из закрытого обращения (БЗ-26): ушёл на проверку владельцу раздела */
export const TicketKbDraft = z.object({
  articleId: z.string(),
  versionId: z.string(),
  /** СТ-0012 */
  number: z.string(),
  /** Текст подготовил Pilot; иначе — вопрос автора и ответы поддержки */
  byPilot: z.boolean(),
});
export type TicketKbDraft = z.infer<typeof TicketKbDraft>;
